import React from "react";

import { API, graphqlOperation } from "aws-amplify";
import { createCarrierWithIdentifiers } from "../../../graphql/carrier/carrier-mutations";
import {
  notifyPrimaryInviteResult,
  sendPrimaryDispatchInvite,
} from "./primaryDispatchInvite";

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
} from "@material-ui/core";

import {
  Button,
  email,
  maxLength,
  minLength,
  regex,
  required,
  TextInput,
  useNotify,
} from "react-admin";
import { Form } from "react-final-form";
import arrayMutators from "final-form-arrays";
import setFieldData from "final-form-set-field-data";

import { CarrierIdentifiersInput } from "../../../components/carrier/CarrierIdentifiersInput";
import { CancelIcon, SaveIcon } from "../../../components/common/iconography";
import { logError } from "../../../utils/appUtils";
import {
  buildCarrierIdentityFields,
  clearCarrierIdentityCache,
  normalizeCarrierIdentifiers,
  validateCarrierUnicity,
} from "../../../utils/carrierIdentity";
import { REGEX_PHONE } from "../../../utils/constants";
import {
  clearCarrierUnicityMeta,
  validateCarrierFieldUnicity,
  validateCarrierIdentityMeta,
} from "./carrierUtils";

// validateCarrierIdentityMeta comes last on each: the field's own rules go
// first, so a malformed email is called malformed rather than checked for
// duplicates it could never have.
const validateBusinessName = [required(), validateCarrierIdentityMeta];
const validateEmail = [required(), email(), validateCarrierIdentityMeta];
// Optional, as on the manager's Carriers form.
const validatePhone = [
  regex(REGEX_PHONE, "Must be valid Phone No."),
  minLength(10),
  maxLength(20),
  validateCarrierIdentityMeta,
];

export const CarrierCreateDialog = ({
  open,
  onClose,
  onSuccess,
  carriers,
  setCarriers,
  setSelectedCarrier,
}) => {
  const notify = useNotify();

  const handleSubmit = async (values) => {
    // Name, email and phone, against every carrier in the yard rather than the
    // ones this dropdown happened to load — the same check the manager's
    // Carriers form runs.
    const duplicate = await validateCarrierUnicity(values);
    if (duplicate) {
      notify(duplicate.message, "warning");
      return;
    }

    try {
      const carrierInput = {
        active: "true",
        reloadYardId: window.appUser?.reloadYardId,
        groups: window.appUser?.groups,
        businessName: values.businessName,
        email: values.email.toLowerCase(),
        phone: values.phone,
        ...buildCarrierIdentityFields(values),
        // Left off the input entirely when the form holds none, rather than
        // written as an empty list.
        identifiers: normalizeCarrierIdentifiers(values.identifiers),
      };

      const response = await API.graphql(
        graphqlOperation(createCarrierWithIdentifiers, { input: carrierInput })
      );

      const newCarrier = response.data.createCarrier;
      // The book the blur checks read is now a carrier short of the truth.
      clearCarrierIdentityCache();
      setCarriers(carriers ? [...carriers, newCarrier] : [newCarrier]);
      setSelectedCarrier(newCarrier);
      // Onboarding: same primary-user invite as the Carriers form. The carrier
      // stands either way; Send Invite on the carrier retries a failure.
      try {
        const result = await sendPrimaryDispatchInvite(newCarrier);
        notifyPrimaryInviteResult(notify, newCarrier, result);
      } catch (error) {
        logError("carriers:CarrierCreateDialog:primaryInvite", error, false);
        notify(
          "Carrier created, but the invite could not be sent. Use Send Invite on the carrier to retry.",
          "warning",
        );
      }
      onSuccess(newCarrier);
    } catch (error) {
      logError("carriers:CarrierCreateDialog:handleSubmit", error, notify);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} disableEnforceFocus>
      <DialogTitle>Create New Carrier</DialogTitle>
      <Form
        onSubmit={handleSubmit}
        // arrayMutators is what CarrierIdentifiersInput's ArrayInput adds and
        // removes rows with. react-admin registers them on its own SimpleForm,
        // so the manager's Carriers form needs no equivalent — this dialog is a
        // bare react-final-form Form and has to ask for them.
        mutators={{ ...arrayMutators, setFieldData }}
        render={({ handleSubmit: formHandleSubmit, form }) => {
          const handleSave = (e) => {
            e.preventDefault();
            // The blur checks' cached verdicts do not get a vote here: a stale
            // one would keep the form invalid and stop handleSubmit's fresh
            // check from ever running.
            clearCarrierUnicityMeta(form);
            if (form.getState().invalid)
              notify(
                "Please correct the highlighted fields before saving.",
                "warning",
              );
            formHandleSubmit(e);
          };
          return (
          <form onSubmit={handleSave} noValidate>
            <DialogContent>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <TextInput
                    source="businessName"
                    label="Business Name"
                    variant="outlined"
                    fullWidth
                    validate={validateBusinessName}
                    onBlur={async () => {
                      await validateCarrierFieldUnicity("businessName", form);
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextInput
                    source="email"
                    type="email"
                    label="Email"
                    variant="outlined"
                    fullWidth
                    parse={(v) => (v !== "" ? v.toLowerCase() : undefined)}
                    validate={validateEmail}
                    onBlur={async () => {
                      await validateCarrierFieldUnicity("email", form);
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextInput
                    source="phone"
                    type="tel"
                    label="Phone"
                    variant="outlined"
                    fullWidth
                    validate={validatePhone}
                    onBlur={async () => {
                      await validateCarrierFieldUnicity("phone", form);
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <CarrierIdentifiersInput />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button label="Cancel" color="default" onClick={onClose}>
                <CancelIcon />
              </Button>
              <Button label="Save" type="submit" variant="contained">
                <SaveIcon />
              </Button>
            </DialogActions>
          </form>
          );
        }}
      />
    </Dialog>
  );
};
